/**
 * stages.js — business stages and the CPG legal roadmap.
 *
 * STAGES are the coarse filter a founder picks ("where am I?").
 * ROADMAP is the ordered visual journey; each step names the agreements to consider at that point.
 * Every `documents` id must resolve in documentLibrary.js — the validator enforces this.
 */

export const STAGES = [
  { id: "idea", name: "Idea", order: 0, blurb: "Nothing built yet. Protecting the concept and setting up ownership." },
  { id: "development", name: "Development", order: 1, blurb: "Formulating the product and working with labs, flavor houses and designers." },
  { id: "pre-launch", name: "Pre-Launch", order: 2, blurb: "Product is close. Lining up manufacturing, packaging and first inventory." },
  { id: "manufacturing", name: "Manufacturing", order: 3, blurb: "Running production with a co-packer and managing suppliers." },
  { id: "launch", name: "Launch", order: 4, blurb: "First sales. Website, 3PL, early retail and direct accounts." },
  { id: "distribution", name: "Distribution", order: 5, blurb: "Distributors, brokers and chain retail." },
  { id: "growth", name: "Growth", order: 6, blurb: "Scaling the team, marketing spend, licensing and new channels." },
  { id: "fundraising", name: "Fundraising", order: 7, blurb: "Raising outside capital or taking on debt." },
  { id: "exit", name: "Exit", order: 8, blurb: "Selling the brand or the company." },
];

export const STAGE_BY_ID = Object.fromEntries(STAGES.map((s) => [s.id, s]));
export const STAGE_IDS = STAGES.map((s) => s.id);

export const IMPORTANCE = [
  { id: "essential", name: "Essential", blurb: "Most CPG brands need this. Going without it is a real, common source of loss.", weight: 0 },
  { id: "common", name: "Common", blurb: "Standard once you reach the relevant activity.", weight: 1 },
  { id: "situational", name: "Situational", blurb: "Only if your specific situation calls for it.", weight: 2 },
  { id: "advanced", name: "Advanced", blurb: "Later-stage or sophisticated transactions.", weight: 3 },
];

export const IMPORTANCE_BY_ID = Object.fromEntries(IMPORTANCE.map((i) => [i.id, i]));
export const IMPORTANCE_IDS = IMPORTANCE.map((i) => i.id);

export const ATTORNEY_LEVELS = [
  { id: "yes", name: "Attorney review recommended", tone: "amber" },
  { id: "strongly-recommended", name: "Attorney involvement strongly recommended", tone: "red" },
  { id: "depends", name: "Depends on the deal size and stakes", tone: "slate" },
];

export const ATTORNEY_LEVEL_IDS = ATTORNEY_LEVELS.map((a) => a.id);

/**
 * The CPG Legal Roadmap — the visual journey shown on /agreements/roadmap.
 * `documents` are the agreements to consider at that step, in the order a founder meets them.
 */
export const ROADMAP = [
  {
    id: "idea",
    step: 1,
    title: "Idea",
    stage: "idea",
    summary: "Before you tell anyone. Get confidentiality and ownership right while it is still cheap.",
    documents: ["mutual-nda", "one-way-nda", "founder-agreement"],
    watchOut:
      "Most founders skip paper at this stage because nothing feels real yet. The two things that actually matter later — who owns the idea, and who you already showed it to — are both decided here.",
  },
  {
    id: "formation",
    step: 2,
    title: "Company Formation",
    stage: "idea",
    summary: "Pick the entity and paper it properly.",
    documents: ["llc-operating-agreement", "corporate-bylaws", "board-consent", "officer-appointment-resolution"],
    watchOut:
      "Filing with the state creates the entity. It does not create the operating agreement, the bylaws, or any record of who owns what. Those are separate documents founders routinely never sign.",
  },
  {
    id: "founders",
    step: 3,
    title: "Founders & Ownership",
    stage: "idea",
    summary: "Split equity, set vesting, and move personal IP into the company.",
    documents: [
      "co-founder-agreement",
      "founder-vesting-agreement",
      "founder-ip-assignment",
      "founder-stock-purchase-agreement",
      "shareholder-agreement",
    ],
    watchOut:
      "A co-founder who leaves in month five while holding a third of the company un-vested is the single most common reason an otherwise good CPG brand becomes unfundable.",
  },
  {
    id: "trademark",
    step: 4,
    title: "Trademark & IP",
    stage: "idea",
    summary: "Secure the brand name and put ownership of every creative asset in the company's hands.",
    documents: ["trademark-assignment", "copyright-assignment", "ip-assignment-agreement", "work-for-hire-agreement"],
    watchOut:
      "The designer who made your logo owns it until they assign it in writing. A commission and an invoice are not an assignment.",
  },
  {
    id: "product-development",
    step: 5,
    title: "Product Development",
    stage: "development",
    summary: "Engage the people who will help you build the product — under confidentiality, with ownership settled up front.",
    documents: ["product-development-agreement", "product-development-nda", "rd-agreement", "consultant-agreement"],
    watchOut: "Ownership of what gets invented is decided before the work starts, not after it succeeds.",
  },
  {
    id: "formulation",
    step: 6,
    title: "Formulation",
    stage: "development",
    summary: "Lock down the formula: who owns it, who can reuse it, and whether you can take it elsewhere.",
    documents: [
      "formula-development-agreement",
      "formula-ownership-agreement",
      "flavor-house-agreement",
      "stability-testing-agreement",
      "shelf-life-testing-agreement",
    ],
    watchOut:
      "If the flavor house owns the formula, you do not have a product — you have a purchase order that can be cancelled.",
  },
  {
    id: "suppliers",
    step: 7,
    title: "Suppliers",
    stage: "pre-launch",
    summary: "Ingredients and raw materials, with the terms that decide your landed cost.",
    documents: ["ingredient-supply-agreement", "raw-material-supply-agreement", "master-supply-agreement", "supplier-quality-agreement"],
    watchOut: "Price escalation clauses and minimum purchase commitments outlive the enthusiasm that made you sign them.",
  },
  {
    id: "manufacturing",
    step: 8,
    title: "Manufacturing",
    stage: "manufacturing",
    summary: "The co-packer relationship — the contract your entire business depends on.",
    documents: ["co-packer-agreement", "manufacturing-nda", "quality-agreement", "product-specification-sheet", "recall-responsibility-agreement"],
    watchOut:
      "Read the termination and transition-assistance clauses before the pricing. Pricing you can renegotiate; being unable to move production you cannot.",
  },
  {
    id: "packaging",
    step: 9,
    title: "Packaging",
    stage: "manufacturing",
    summary: "Cans, bottles, labels and cartons — long lead times and large minimums.",
    documents: ["packaging-supply-agreement", "can-supply-agreement", "label-supply-agreement", "packaging-artwork-ownership"],
    watchOut:
      "A packaging change forced by a co-packer or a label revision forced by regulation can strand a full minimum order. Decide in advance who eats obsolete inventory.",
  },
  {
    id: "logistics",
    step: 10,
    title: "Warehousing & 3PL",
    stage: "launch",
    summary: "Where inventory lives and who is accountable when it goes missing.",
    documents: ["three-pl-agreement", "warehouse-agreement", "fulfillment-agreement", "transportation-agreement"],
    watchOut: "3PL fee schedules are where margin quietly disappears. Model the per-order cost before signing, not after.",
  },
  {
    id: "brokers",
    step: 11,
    title: "Brokers",
    stage: "distribution",
    summary: "Representation into retail — and the commission mechanics behind it.",
    documents: ["cpg-broker-agreement", "retail-broker-agreement", "commission-sales-agreement"],
    watchOut: "Define house accounts on day one, or you will pay commission on business you brought in yourself.",
  },
  {
    id: "distributors",
    step: 12,
    title: "Distributors",
    stage: "distribution",
    summary: "The highest-stakes agreement most CPG founders sign.",
    documents: ["beverage-distributor-agreement", "food-distributor-agreement", "dsd-distributor-agreement", "exclusive-distribution-agreement"],
    watchOut:
      "Exclusivity without performance minimums and a clean termination right can freeze a territory for years while producing almost no sales.",
  },
  {
    id: "retailers",
    step: 13,
    title: "Retailers",
    stage: "distribution",
    summary: "Vendor agreements and the deduction landscape behind every purchase order.",
    documents: ["retail-vendor-agreement", "wholesale-supply-agreement", "promotional-allowance-agreement", "slotting-placement-agreement"],
    watchOut:
      "The purchase order is not the deal. The vendor agreement, the deduction policy and the routing guide are the deal.",
  },
  {
    id: "marketing",
    step: 14,
    title: "Marketing & Influencers",
    stage: "growth",
    summary: "Agencies, creators and content — and who owns what they make.",
    documents: ["marketing-agency-agreement", "influencer-agreement", "ugc-creator-agreement", "photography-agreement", "content-creator-agreement"],
    watchOut:
      "If you cannot run the creator's content as a paid ad, you bought a post, not an asset. Usage rights and whitelisting are separate grants.",
  },
  {
    id: "capital",
    step: 15,
    title: "Capital Raising",
    stage: "fundraising",
    summary: "SAFEs, notes and priced rounds — securities documents, start to finish.",
    documents: ["safe", "convertible-note", "term-sheet", "stock-purchase-agreement", "investor-rights-agreement"],
    watchOut:
      "This is the one stage where using a template without counsel creates legal exposure rather than just commercial risk. Selling securities is regulated.",
  },
  {
    id: "growth",
    step: 16,
    title: "Growth",
    stage: "growth",
    summary: "Team, licensing, partnerships and international expansion.",
    documents: ["employment-agreement", "independent-contractor-agreement", "trademark-license-agreement", "co-branding-agreement", "international-distributor-agreement"],
    watchOut: "Every new channel brings its own contract. Growth is where unpapered handshakes accumulate fastest.",
  },
  {
    id: "exit",
    step: 17,
    title: "Acquisition / Exit",
    stage: "exit",
    summary: "Diligence will surface every agreement you never signed.",
    documents: ["letter-of-intent", "due-diligence-nda", "asset-purchase-agreement", "ip-purchase-agreement", "transition-services-agreement"],
    watchOut:
      "Buyers discount for missing paper. Unassigned IP, undocumented equity and an un-terminable distributor agreement all come out of the purchase price.",
  },
];

export const ROADMAP_BY_ID = Object.fromEntries(ROADMAP.map((r) => [r.id, r]));
